import React from "react";
import { PropTypes } from "prop-types";
import Heading from "@amzn/meridian/heading";
import Tag from "@amzn/meridian/tag";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Divider from "@amzn/meridian/divider";
import Link from "@amzn/meridian/link";
import moment from "moment";
import { FormattedMessage, intlShape, injectIntl } from "react-intl";
import {
  getTranslatedProcessName,
  getTaskName,
  isValidTimestamp,
  priorityMapper,
  getEffectivePriority,
} from "../../../utils/Helper";
import { EXCLUDED_TASK_ATTRIBUTES } from "../../../utils/Constants";
import { useDeviceLayout } from "../../common/context/DeviceLayoutContext";
import ImpactedPackages from "./ImpactedPackages";

function TaskAttributes({ task, intl }) {
  const { headerFont, isMobile } = useDeviceLayout();

  const timestampFormat = "MMM DD, YYYY, hh:mm A";
  const timezone = moment.tz(moment.tz.guess()).zoneAbbr();

  const commonAttributes = [
    { label: "Task Title", value: getTaskName(task) },
    {
      label: "Task Category",
      value: getTranslatedProcessName(task.category, intl),
    },
  ];
  if (
    task.additionalAttributes.expiresAt &&
    isValidTimestamp(task.additionalAttributes.expiresAt.attributeValue)
  ) {
    const expiresTime = moment(
      task.additionalAttributes.expiresAt.attributeValue,
      "x"
    );
    const expiresIn = expiresTime.from(new Date());
    commonAttributes.push({
      label: "Expiry",
      value: `${expiresIn} - ${expiresTime.format(
        timestampFormat
      )} ${timezone}`,
    });
  }
  if (isValidTimestamp(Number(task.createdTime) * 1000)) {
    commonAttributes.push({
      label: "Generated on",
      value:
        moment(Number(task.createdTime) * 1000, "x").format(timestampFormat) +
        " " +
        timezone,
    });
  }

  const gridWidth = isMobile ? ["fit"] : ["25%", "75%"];

  const packageCount = task.additionalAttributes && 
    task.additionalAttributes.impactedPackageCount &&
    task.additionalAttributes.impactedPackageCount.attributeValue;
  
  const trackingIds = task.additionalAttributes && 
    task.additionalAttributes.impactedPackageTrackingIds &&
    task.additionalAttributes.impactedPackageTrackingIds.attributeValue;

  // Mapping Priority to appropriate color label using CRITICALITY if available
  const effectivePriority = getEffectivePriority(task);
  const priority = priorityMapper(effectivePriority, intl);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        paddingRight: "2.5%",
      }}
    >
      {/* Task Details */}
      {/* Heading */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <Heading level={1}>
          <FormattedMessage
            id="taskDetails.taskDetails"
            defaultMessage="Task Details"
          />
        </Heading>
        <Tag type={priority.status}>{priority.label}</Tag>
      </div>
      {/* Common Task Attributes */}
      <div>
        <Box type="fill" spacingInset="400">
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {commonAttributes.map((item) => {
              const { label, value } = item;
              return (
                <Row wrap="down" key={label} width="100%" widths={gridWidth}>
                  <Text type={headerFont}>{label}</Text>
                  <Text>{value}</Text>
                </Row>
              );
            })}
          </div>
        </Box>
      </div>
      <Divider />
      {/* Additional Details */}
      <Heading level={1}>
        <FormattedMessage
          id="taskDetails.additionalDetails"
          defaultMessage="Additional Details"
        />
      </Heading>
      <div>
        <Box type="fill" spacingInset="400">
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {Object.keys(task.additionalAttributes)
              .filter((key) => !EXCLUDED_TASK_ATTRIBUTES.includes(key))
              .map((key) => task.additionalAttributes[key])
              .map((attribute) => {
                const label = attribute.attributeLabel;
                const value = attribute.attributeValue;
                if (attribute.isForDisplay !== true) return null;
                if (!value) return null;
                return (
                  <Row
                    wrap="down"
                    alignmentVertical="top"
                    key={label}
                    width="100%"
                    alignmentHorizontal="start"
                    widths={gridWidth}
                  >
                    <Text type={headerFont}>{label}</Text>
                    <Text breakWord={true}>{value}</Text>
                  </Row>
                );
              })}
            {/* Impacted Packages Section */}
            <ImpactedPackages 
              packageCount={packageCount}
              trackingIds={trackingIds}
              gridWidth={gridWidth} 
              intl={intl} 
            />
            {Array.isArray(task.links) &&
              task.links.map((link) => {
                return (
                  <Row
                    wrap="down"
                    alignmentVertical="top"
                    key={link.label}
                    widths={gridWidth}
                  >
                    <Text type={headerFont}>{link.label}</Text>
                    <Link href={link.url} target="_blank" rel="noopener noreferrer">
                      <div
                        style={{
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {link.url}
                      </div>
                    </Link>
                  </Row>
                );
              })}
          </div>
        </Box>
      </div>
    </div>
  );
}

TaskAttributes.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
    priority: PropTypes.string,
    additionalAttributes: PropTypes.arrayOf(
      PropTypes.shape({
        attributeLabel: PropTypes.string,
        attributeValue: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
      })
    ),
    createdTime: PropTypes.number,
    dueDateTime: PropTypes.number,
  }).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(TaskAttributes);



// WEBPACK FOOTER //
// ./src/components/taskExecution/TaskAttributes/TaskAttributes.jsx